import { Flex, FloatingIndicator, Tabs, TabsProps, Text } from '@mantine/core';
import React, { ReactNode, useState } from 'react';
import classes from './UnderlineTabs.module.css';

interface UnderlineTabsProps {
  title?: string;
  children: ReactNode;
  onChange: TabsProps['onChange'];
  value: TabsProps['value'];
}

interface FloatingListProps {
  children: ReactNode;
  className?: string;
  value?: string | null;
}

interface UnderlineTabProps {
  value: string;
  ref?: React.Ref<HTMLButtonElement>;
}

function UnderlineTabsDeprecated({ title, children, onChange, value }: UnderlineTabsProps) {
  const childrenArray = React.Children.toArray(children);

  const tabsList = childrenArray
    .filter(
      (child): child is React.ReactElement =>
        React.isValidElement(child) && child.type === UnderlineTabsDeprecated.List,
    )
    .map(child => React.cloneElement(child, { value } as Partial<FloatingListProps>));

  const otherContent = childrenArray.filter(
    child => !React.isValidElement(child) || child.type !== UnderlineTabsDeprecated.List,
  );

  return (
    <Tabs
      value={value}
      onChange={onChange}
      variant="underline"
      classNames={{
        list: classes.list,
        tab: classes.tab,
        tabLabel: classes.tabLabel,
      }}
    >
      {tabsList.length > 0 && (
        <Flex direction={'row'}>
          {title && (
            <Flex
              direction={'row'}
              align={'center'}
              mb={'lg'}
              justify={'space-between'}
              mr={'xxxl'}
              ml={'sm'}
            >
              <Text size={'xxl'} fw={600}>
                {title}
              </Text>
            </Flex>
          )}

          {tabsList.length > 0 && (
            <Flex direction={'row'} align={'center'} mb={'md'}>
              {tabsList}
            </Flex>
          )}
        </Flex>
      )}

      {otherContent}
    </Tabs>
  );
}

UnderlineTabsDeprecated.List = function UnderlineTabsList({
  children,
  value,
}: Omit<FloatingListProps, 'className'>) {
  const [rootRef, setRootRef] = useState<HTMLDivElement | null>(null);
  const [controlsRefs, setControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({});

  const setControlRef = (val: string) => (node: HTMLButtonElement) => {
    controlsRefs[val] = node;
    setControlsRefs(controlsRefs);
  };

  const tabElements = React.Children.toArray(children)
    .filter(
      (child): child is React.ReactElement =>
        React.isValidElement(child) && child.type === UnderlineTabsDeprecated.Tab,
    )
    .map(child =>
      React.cloneElement(child, {
        ref: setControlRef(child.props.value),
      } as Partial<UnderlineTabProps>),
    );

  return (
    <Tabs.List ref={setRootRef}>
      {tabElements}
      <FloatingIndicator
        target={value ? controlsRefs[value] : null}
        parent={rootRef}
        className={classes.indicator}
      />
    </Tabs.List>
  );
};

UnderlineTabsDeprecated.Tab = Tabs.Tab;
UnderlineTabsDeprecated.Panel = Tabs.Panel;

export default UnderlineTabsDeprecated;
